import React, { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { colors } from "utils";

const Wrapper = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.div`
  color: ${colors.nightlyWoods900 || "#111827"};
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const Toggle = styled.div`
  background: ${colors.milkGlass500};
  border: 1px solid ${colors.nightlyWoods100};
  border-radius: 6px;
  display: inline-flex;
  overflow: hidden;
  padding: 2px;
`;

const ToggleButton = styled.button`
  background: ${({ $active }) => ($active ? colors.nightlyWoods100 : "transparent")};
  border: none;
  border-radius: 4px;
  color: ${({ $active }) => ($active ? colors.nightlyWoods900 : colors.milkGlass950)};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  transition: background 0.15s ease, color 0.15s ease;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${colors.blue300};
  }

  &:hover:not(:disabled) {
    color: ${({ $active }) => ($active ? colors.white : colors.nightlyWoods700)};
  }
`;

const Empty = styled.div`
  color: ${colors.nightlyWoods700 || "#6B7280"};
  font-size: 13px;
  padding: 16px;
  text-align: center;
`;

const LEVEL_DEFS = [
  { level: 1, label: "Level 1: Needs Intervention", color: "#d97676" },
  { level: 2, label: "Level 2: Needs Guidance", color: "#e9c46a" },
  { level: 3, label: "Level 3: Effective Collaboration", color: "#16a34a" },
  { level: 4, label: "Level 4: Exceeding Expectations", color: "#3b82f6" },
];

const SCORE_KEY_CANDIDATES = [
  "group_collaboration_score",
  "collaboration_score",
  "discourse_score",
  "group_score",
  "student_collaboration_score",
  "student_score",
  "participant_score",
  "collaboration_level",
  "score",
  "level",
];

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad2 = (n) => String(n).padStart(2, "0");

const monthBucket = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const key = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
  const label = `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return { key, label };
};

const weekBucket = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + diffToMonday)
  );
  const y = monday.getUTCFullYear();
  const m = monday.getUTCMonth();
  const dd = monday.getUTCDate();
  const key = `${y}-${pad2(m + 1)}-${pad2(dd)}`;
  const label = `${MONTH_LABELS[m]} ${dd}`;
  return { key, label };
};

const detectScoreKey = (rows, override) => {
  if (override) return override;
  if (!rows || rows.length === 0) return null;
  const keys = new Set();
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
  for (const candidate of SCORE_KEY_CANDIDATES) {
    if (!keys.has(candidate)) continue;
    const sample = rows.find(
      (r) => r && typeof r[candidate] === "number" && Number.isFinite(r[candidate])
    )?.[candidate];
    if (sample !== undefined) return candidate;
  }
  return null;
};

export const GroupCollabChart = ({
  rows,
  dateKey = "session_date",
  scoreKey: scoreKeyOverride,
  title = "Group Math Discourse & Collaboration Scores",
}) => {
  const [granularity, setGranularity] = useState("week");

  const scoreKey = useMemo(
    () => detectScoreKey(rows, scoreKeyOverride),
    [rows, scoreKeyOverride]
  );

  const data = useMemo(() => {
    if (!rows || rows.length === 0 || !scoreKey) return [];
    const bucketFn = granularity === "month" ? monthBucket : weekBucket;
    const byBucket = new Map();
    rows.forEach((row) => {
      const bucket = bucketFn(row?.[dateKey]);
      const score = row?.[scoreKey];
      if (!bucket || typeof score !== "number" || !Number.isFinite(score)) return;
      const level = Math.max(1, Math.min(4, Math.round(score)));
      if (!byBucket.has(bucket.key)) {
        byBucket.set(bucket.key, {
          key: bucket.key,
          label: bucket.label,
          count: 0,
          sum: 0,
          counts: { 1: 0, 2: 0, 3: 0, 4: 0 },
        });
      }
      const entry = byBucket.get(bucket.key);
      entry.count += 1;
      entry.sum += score;
      entry.counts[level] += 1;
    });

    return Array.from(byBucket.values())
      .map((e) => {
        const out = {
          key: e.key,
          bucket: e.label,
          avg: e.count ? Number((e.sum / e.count).toFixed(2)) : 0,
        };
        LEVEL_DEFS.forEach(({ level }) => {
          out[`Level ${level}`] = e.count
            ? Number(((e.counts[level] / e.count) * 100).toFixed(2))
            : 0;
        });
        return out;
      })
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  }, [rows, dateKey, scoreKey, granularity]);

  if (!rows || rows.length === 0) return null;

  const toggle = (
    <Toggle role="group" aria-label="Chart granularity">
      <ToggleButton
        type="button"
        $active={granularity === "week"}
        onClick={() => setGranularity("week")}
      >
        Week
      </ToggleButton>
      <ToggleButton
        type="button"
        $active={granularity === "month"}
        onClick={() => setGranularity("month")}
      >
        Month
      </ToggleButton>
    </Toggle>
  );

  if (!scoreKey || data.length === 0) {
    return (
      <Wrapper>
        <Header>
          <Title>{title}</Title>
          {toggle}
        </Header>
        <Empty>
          Could not find a 1–4 score field on these rows. Tried:{" "}
          {SCORE_KEY_CANDIDATES.join(", ")}.
        </Empty>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <Title>{title}</Title>
        {toggle}
      </Header>
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={data} margin={{ top: 24, right: 24, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="left"
            domain={[0, 100]}
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => `${v}%`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 4]}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value, name) =>
              name && name.startsWith("Level ")
                ? [`${Number(value).toFixed(1)}%`, name]
                : [Number(value).toFixed(2), name]
            }
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {LEVEL_DEFS.map((def) => (
            <Bar
              key={def.level}
              yAxisId="left"
              dataKey={`Level ${def.level}`}
              name={def.label}
              stackId="levels"
              fill={def.color}
              isAnimationActive={false}
            />
          ))}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="avg"
            name="Avg Score (Right Axis)"
            stroke="#111111"
            strokeWidth={3}
            dot={{ r: 4, fill: "#111111" }}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="avg"
              position="top"
              formatter={(v) => Number(v).toFixed(1)}
              style={{ fontSize: 12, fontWeight: 600, fill: "#111111" }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};
